import type { AlertDto } from '@citilight-monitor/contracts';
import { cn } from '../../lib/cn';
import { formatClock } from '../../lib/time';
import { Card } from '../atoms';

const HOUR = 3_600_000;
const HOURS = 24;

interface AlertActivityProps {
  alerts: AlertDto[];
  /** False until every client's alerts have loaded. */
  ready: boolean;
  now?: number;
}

/** Alerts sent per hour over the last day, coloured by the worst severity in each hour. */
export function AlertActivity({ alerts, ready, now = Date.now() }: AlertActivityProps) {
  const start = now - HOURS * HOUR;
  const buckets = Array.from({ length: HOURS }, (_, i) => ({ t: start + i * HOUR, count: 0, critical: false }));
  for (const alert of alerts) {
    const i = Math.floor((Date.parse(alert.sentAt) - start) / HOUR);
    const bucket = buckets[i];
    if (!bucket) continue;
    bucket.count += 1;
    bucket.critical ||= alert.severity === 'critical';
  }
  const max = Math.max(1, ...buckets.map((b) => b.count));
  const failed = alerts.filter((a) => !a.delivered).length;

  return (
    <Card as="section" raised aria-label="Alerts sent in the last 24 hours" className="flex flex-col gap-4 px-6 pb-5 pt-5">
      <div className="flex items-baseline justify-between gap-3">
        <h2 className="text-xl">Alerts sent</h2>
        <span className="text-[13px] text-fg-muted">last 24 h</span>
      </div>

      <div className="flex flex-col gap-1.5">
        <div className="flex h-28 items-end gap-[3px] border-b border-line" aria-hidden>
          {buckets.map((b) => (
            <div
              key={b.t}
              title={`${formatClock(b.t)}: ${b.count} alert${b.count === 1 ? '' : 's'}`}
              className={cn('flex-1 rounded-t-[3px]', b.count === 0 ? 'bg-grid' : b.critical ? 'bg-critical' : 'bg-warning')}
              style={{ height: b.count === 0 ? 4 : `${Math.max(14, (b.count / max) * 100)}%` }}
            />
          ))}
        </div>
        <div className="flex justify-between font-mono text-[11px] text-fg-muted" aria-hidden>
          {[0, 6, 12, 18].map((i) => (
            <span key={i}>{formatClock(buckets[i]!.t)}</span>
          ))}
          <span>now</span>
        </div>
      </div>

      <div className="flex flex-wrap gap-x-5 gap-y-1 text-[13px] text-fg-secondary">
        {ready ? (
          <>
            <span>
              <b className="font-mono text-fg">{alerts.length}</b> sent
            </span>
            <span>
              <b className={cn('font-mono', failed ? 'text-chip-critical-fg' : 'text-fg')}>{failed}</b> failed to deliver
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className="size-2 rounded-[2px] bg-critical" aria-hidden />
              critical
              <span className="ml-2 size-2 rounded-[2px] bg-warning" aria-hidden />
              warning
            </span>
          </>
        ) : (
          <span className="text-fg-muted">Loading alerts…</span>
        )}
      </div>
    </Card>
  );
}
